/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Badge,
  Accordion,
  Table,
  Modal
} from 'react-bootstrap';
import { FaStar, FaSearch, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * CustomerManagement - View customer reviews and blacklist status
 */
export default function CustomerManagement() {
  const [searchType, setSearchType] = useState('phone'); // 'phone' or 'email'
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [blacklistData, setBlacklistData] = useState(null);
  const [allBlacklist, setAllBlacklist] = useState([]);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [blacklistToRemove, setBlacklistToRemove] = useState(null);
  
  // Manual blacklist entry
  const [showAddBlacklistModal, setShowAddBlacklistModal] = useState(false);
  const [addBlacklistForm, setAddBlacklistForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    reason: ''
  });
  const [addingBlacklist, setAddingBlacklist] = useState(false);

  // Load all blacklisted customers on mount
  useEffect(() => {
    fetchAllBlacklist();
  }, []);

  const fetchAllBlacklist = async () => {
    try {
      setLoadingBlacklist(true);
      const response = await axios.get('/customer-reviews/blacklist');
      setAllBlacklist(response.data.blacklist || []);
    } catch (err) {
      console.error('Error fetching blacklist:', err);
    } finally {
      setLoadingBlacklist(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      toast.error('Please enter a phone number or email');
      return;
    }

    try {
      setLoading(true);
      setCustomerData(null);
      setBlacklistData(null);

      const params = searchType === 'phone' 
        ? { phone: searchValue.trim() }
        : { email: searchValue.trim() };

      // Fetch reviews
      const reviewsResponse = await axios.get('/customer-reviews/customer', { params });
      setCustomerData(reviewsResponse.data);

      // Check blacklist status
      const blacklistResponse = await axios.get('/customer-reviews/blacklist/check', { params });
      setBlacklistData(blacklistResponse.data);

    } catch (err) {
      console.error('Error searching customer:', err);
      toast.error(err.response?.data?.error || 'Failed to search customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromBlacklist = async () => {
    if (!blacklistToRemove) return;

    try {
      await axios.delete(`/customer-reviews/blacklist/${blacklistToRemove._id}`);
      toast.success('Customer removed from blacklist');
      setShowRemoveModal(false);
      setBlacklistToRemove(null);
      
      // Refresh data
      if (searchValue) {
        handleSearch({ preventDefault: () => {} });
      }
      fetchAllBlacklist();
    } catch (err) {
      console.error('Error removing from blacklist:', err);
      toast.error(err.response?.data?.error || 'Failed to remove from blacklist');
    }
  };

  const handleAddBlacklist = async (e) => {
    e.preventDefault();
    
    // Validate form - only name and phone required
    if (!addBlacklistForm.customerName.trim() || 
        !addBlacklistForm.customerPhone.trim()) {
      toast.error('Customer name and phone number are required');
      return;
    }

    try {
      setAddingBlacklist(true);
      await axios.post('/customer-reviews/blacklist', {
        customerName: addBlacklistForm.customerName.trim(),
        customerPhone: addBlacklistForm.customerPhone.trim(),
        customerEmail: addBlacklistForm.customerEmail.trim() || 'no-email@provided.com',
        reason: addBlacklistForm.reason.trim() || 'No reason provided'
      });
      
      toast.success('Customer added to blacklist');
      setShowAddBlacklistModal(false);
      setAddBlacklistForm({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        reason: ''
      });
      
      // Refresh blacklist
      fetchAllBlacklist();
    } catch (err) {
      console.error('Error adding to blacklist:', err);
      toast.error(err.response?.data?.error || 'Failed to add to blacklist');
    } finally {
      setAddingBlacklist(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="d-inline-flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            size={20}
            color={star <= rating ? '#ffc107' : '#e4e5e9'}
          />
        ))}
        <span className="ms-2 fw-bold">{rating}/5</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container className="py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <h2 className="mb-4">👥 Customer Management</h2>

      {/* Search Section */}
      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: '#007bff', color: 'white' }}>
          <h5 className="mb-0">🔍 Search Customer</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Search By</Form.Label>
                  <Form.Select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <option value="phone">Phone Number</option>
                    <option value="email">Email Address</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group>
                  <Form.Label>
                    {searchType === 'phone' ? 'Phone Number' : 'Email Address'}
                  </Form.Label>
                  <Form.Control
                    type={searchType === 'phone' ? 'tel' : 'email'}
                    placeholder={searchType === 'phone' ? 'e.g., 01234567890' : 'e.g., customer@email.com'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch className="me-2" />
                      Search
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Search Results */}
      {customerData && (
        <>
          {/* Blacklist Status */}
          {blacklistData && (
            <Alert 
              variant={blacklistData.isBlacklisted ? 'danger' : 'success'}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                {blacklistData.isBlacklisted ? (
                  <>
                    <FaExclamationTriangle className="me-2" />
                    <strong>This customer is BLACKLISTED</strong>
                    {blacklistData.blacklist && (
                      <div className="mt-2">
                        <small>
                          <strong>Reason:</strong> {blacklistData.blacklist.reason}
                          <br />
                          <strong>Added by:</strong> {blacklistData.blacklist.addedByDriverId?.companyName || blacklistData.blacklist.addedByDriverId?.email}
                          <br />
                          <strong>Date:</strong> {formatDate(blacklistData.blacklist.createdAt)}
                        </small>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="me-2" />
                    <strong>This customer is NOT blacklisted</strong>
                  </>
                )}
              </div>
              {blacklistData.isBlacklisted && blacklistData.blacklist && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    setBlacklistToRemove(blacklistData.blacklist);
                    setShowRemoveModal(true);
                  }}
                >
                  Remove from Blacklist
                </Button>
              )}
            </Alert>
          )}

          {/* Reviews Summary */}
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: '#28a745', color: 'white' }}>
              <h5 className="mb-0">⭐ Reviews Summary</h5>
            </Card.Header>
            <Card.Body>
              {customerData.totalReviews > 0 ? (
                <>
                  <Row className="text-center mb-3">
                    <Col md={6}>
                      <h3>{customerData.averageRating.toFixed(1)}</h3>
                      {renderStars(Math.round(customerData.averageRating))}
                      <p className="text-muted mt-2">Average Rating</p>
                    </Col>
                    <Col md={6}>
                      <h3>{customerData.totalReviews}</h3>
                      <p className="text-muted">Total Reviews</p>
                    </Col>
                  </Row>

                  <hr />

                  <h6 className="mb-3">Review History</h6>
                  <Accordion>
                    {customerData.reviews.map((review, index) => (
                      <Accordion.Item key={review._id} eventKey={index.toString()}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between w-100 me-3">
                            <div>
                              {renderStars(review.starRating)}
                            </div>
                            <div className="text-muted">
                              <small>{formatDate(review.createdAt)}</small>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table bordered size="sm">
                            <tbody>
                              <tr>
                                <td><strong>Driver</strong></td>
                                <td>
                                  {review.driverId?.companyName || 
                                   `${review.driverId?.firstName} ${review.driverId?.surname}` ||
                                   review.driverId?.email}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Job Date</strong></td>
                                <td>{review.jobId?.dateTime ? formatDate(review.jobId.dateTime) : 'N/A'}</td>
                              </tr>
                              <tr>
                                <td><strong>Route</strong></td>
                                <td>
                                  {review.jobId?.from} → {review.jobId?.to}
                                </td>
                              </tr>
                              {review.jobId?.fareAmount && (
                                <tr>
                                  <td><strong>Fare</strong></td>
                                  <td>£{review.jobId.fareAmount}</td>
                                </tr>
                              )}
                              {review.reviewText && (
                                <tr>
                                  <td><strong>Notes</strong></td>
                                  <td>{review.reviewText}</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </>
              ) : (
                <Alert variant="info">
                  No reviews found for this customer.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* All Blacklisted Customers */}
      <Card>
        <Card.Header style={{ backgroundColor: '#dc3545', color: 'white' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">⚠️ Blacklisted Customers</h5>
            <Button 
              variant="light" 
              size="sm"
              onClick={() => setShowAddBlacklistModal(true)}
            >
              ➕ Add to Blacklist
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loadingBlacklist ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading blacklist...</p>
            </div>
          ) : allBlacklist.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Reason</th>
                  <th>Added By</th>
                  <th>Date Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allBlacklist.map((entry) => (
                  <tr key={entry._id}>
                    <td>{entry.customerName}</td>
                    <td>{entry.customerPhone}</td>
                    <td>{entry.customerEmail}</td>
                    <td>
                      <small>{entry.reason.substring(0, 50)}
                      {entry.reason.length > 50 && '...'}</small>
                    </td>
                    <td>
                      <small>
                        {entry.addedByDriverId?.companyName || 
                         entry.addedByDriverId?.email}
                      </small>
                    </td>
                    <td>
                      <small>{formatDate(entry.createdAt)}</small>
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setBlacklistToRemove(entry);
                          setShowRemoveModal(true);
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="success">
              No customers are currently blacklisted.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Remove from Blacklist Confirmation Modal */}
      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Remove from Blacklist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {blacklistToRemove && (
            <>
              <p>Are you sure you want to remove this customer from the blacklist?</p>
              <div className="bg-light p-3 rounded">
                <strong>Name:</strong> {blacklistToRemove.customerName}<br />
                <strong>Phone:</strong> {blacklistToRemove.customerPhone}<br />
                <strong>Email:</strong> {blacklistToRemove.customerEmail}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemoveFromBlacklist}>
            Remove from Blacklist
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add to Blacklist Modal */}
      <Modal 
        show={showAddBlacklistModal} 
        onHide={() => {
          setShowAddBlacklistModal(false);
          setAddBlacklistForm({
            customerName: '',
            customerPhone: '',
            customerEmail: '',
            reason: ''
          });
        }} 
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
          <Modal.Title>➕ Add Customer to Blacklist</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddBlacklist}>
          <Modal.Body>
            <Alert variant="warning" className="mb-3">
              <strong>Note:</strong> Manually adding a customer to the blacklist will flag them for all drivers in the system.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label><strong>Customer Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter customer name"
                value={addBlacklistForm.customerName}
                onChange={(e) => setAddBlacklistForm(f => ({ ...f, customerName: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Phone Number *</strong></Form.Label>
              <Form.Control
                type="tel"
                placeholder="e.g., 01234567890"
                value={addBlacklistForm.customerPhone}
                onChange={(e) => setAddBlacklistForm(f => ({ ...f, customerPhone: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Email Address</strong> <small className="text-muted">(optional)</small></Form.Label>
              <Form.Control
                type="email"
                placeholder="e.g., customer@example.com"
                value={addBlacklistForm.customerEmail}
                onChange={(e) => setAddBlacklistForm(f => ({ ...f, customerEmail: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><strong>Reason for Blacklisting</strong> <small className="text-muted">(optional)</small></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Optional: Provide a reason for blacklisting this customer..."
                value={addBlacklistForm.reason}
                onChange={(e) => setAddBlacklistForm(f => ({ ...f, reason: e.target.value }))}
                maxLength={1000}
              />
              <small className="text-muted">
                {addBlacklistForm.reason.length}/1000 characters
              </small>
            </Form.Group>

            <Alert variant="info" className="mb-0">
              <small>
                <strong>Important:</strong> This information will be visible to other drivers when they attempt to book this customer.
              </small>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowAddBlacklistModal(false);
                setAddBlacklistForm({
                  customerName: '',
                  customerPhone: '',
                  customerEmail: '',
                  reason: ''
                });
              }}
              disabled={addingBlacklist}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              type="submit"
              disabled={addingBlacklist}
            >
              {addingBlacklist ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Adding...
                </>
              ) : (
                <>⚠️ Add to Blacklist</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

